import type { PageViewport } from 'pdfjs-dist'
import { normalizeUnicode } from 'pdfjs-dist'
import type { TextItem } from 'pdfjs-dist/types/src/display/api.js'

export type NormalizedHighlight = {
    x: number
    y: number
    width: number
    height: number
}

type Glyph = {
    normalized: string
    isWhitespace: boolean
    rect: NormalizedHighlight
}

const unicodeWhitespacePattern = /[\s\u00ad\u2009\u202f\u2007\u200a\u200b\u2060\ufeff]/u
const whitespaceStrippingPattern = /[\s\u00ad\u2009\u202f\u2007\u200a\u200b\u2060\ufeff]+/gu
const thousandsSeparatorPattern = /['’´`\u00a0\u202f\u2009\u2007]/gu
const dashVariantPattern = /[\u2010-\u2015\u2212]/gu
const numericAnchorPattern = /\d[\d.,\s\u00a0\u202f\u2009]*/gu
const omissionMarkerPattern = new RegExp('\\[\\s*(?:\\.\\.\\.|…)?\\s*\\]', 'gu')
const ellipsisPattern = /\s*(?:\.{3}|…)\s*/gu
const sectionSeparatorPattern = /,\s+(?=[A-ZÄÖÜ])/g
const lineSeparatorPattern = /\s*[;|]\s*/g

export const FOCUS_HIGHLIGHT_PAGE_SEARCH_RADIUS = 1
export const FOCUS_HIGHLIGHT_FALLBACK_SEARCH_RADIUS = 6

export const normalizeFocusHighlightTerm = (term: string): string =>
    term
        .replace(/\r\n/g, '\n')
        .replace(omissionMarkerPattern, '\n')
        .replace(ellipsisPattern, '\n')
        .replace(sectionSeparatorPattern, '\n')
        .replace(lineSeparatorPattern, '\n')
        .split(/\r?\n+/)
        .map((line) => line.trim().replace(/\s+/gu, ' '))
        .filter((line) => line.length > 0)
        .join('\n')

const charNormalizationMap: Record<string, string> = {
    '\u2212': '-', // minus sign
    '\u2010': '-', // hyphen
    '\u2011': '-', // non-breaking hyphen
    '\u2012': '-', // figure dash
    '\u2013': '-', // en dash
    '\u2014': '-', // em dash
    '\u2015': '-', // horizontal bar
    '\u2044': '/', // fraction slash
    '\u00b7': '.', // middle dot
    '\u2024': '.', // one dot leader
    '\u2027': '.', // hyphenation point
    '\u00a0': ' ', // no-break space
    '\u202f': ' ', // narrow no-break space
    '\u2009': ' ', // thin space
    '\u2007': ' ', // figure space
    '\u200a': ' ', // hair space
    '\u2060': '', // word joiner
    '\ufeff': '', // zero-width no-break space
    ä: 'a',
    Ä: 'a',
    ö: 'o',
    Ö: 'o',
    ü: 'u',
    Ü: 'u',
    ӓ: 'a',
    Ӓ: 'a',
    а: 'a',
    А: 'a',
    е: 'e',
    Е: 'e',
    о: 'o',
    О: 'o',
    с: 'c',
    С: 'c',
    р: 'p',
    Р: 'p',
    х: 'x',
    Х: 'x',
    і: 'i',
    І: 'i',
    ј: 'j',
    Ј: 'j',
    ',': '',
    '.': '',
    ':': '',
    ';': '',
    '!': '',
    '?': '',
    '(': '',
    ')': '',
    '[': '',
    ']': '',
    '{': '',
    '}': '',
    '"': '',
    '\u2018': '', // left single quotation mark
    '\u2019': '', // right single quotation mark
    '\u201a': '', // single low-9 quotation mark
    '\u201b': '', // single high-reversed-9 quotation mark
    '\u201c': '', // left double quotation mark
    '\u201d': '', // right double quotation mark
    '\u201e': '', // double low-9 quotation mark
    '\u201f': '', // double high-reversed-9 quotation mark
    '\u00ab': '', // left-pointing double angle quotation mark
    '\u00bb': '', // right-pointing double angle quotation mark
    '\u2039': '', // left-pointing single angle quotation mark
    '\u203a': '', // right-pointing single angle quotation mark
    "'": '',
    '´': '',
    '`': '',
}

const isWhitespaceLike = (char: string): boolean => unicodeWhitespacePattern.test(char)

const canonicalizeChar = (char: string): string => {
    const normalized = (normalizeUnicode(char) ?? char) as string
    const substituted = Array.from(normalized, (codePoint) => {
        const replacement = charNormalizationMap[codePoint]
        if (replacement !== undefined) {
            return replacement
        }
        return codePoint
    }).join('')
    const [firstChar] = Array.from(substituted)
    return (firstChar ?? '').toLowerCase()
}

const isSkippableVariantChar = (char: string): boolean => {
    if (isWhitespaceLike(char)) {
        return true
    }
    const normalized = canonicalizeChar(char)
    return normalized.length === 0
}

export const normalizeTextForMatching = (value: string): string =>
    Array.from(value).reduce((normalizedText, char) => {
        const normalizedChar = canonicalizeChar(char)
        if (isWhitespaceLike(char) || normalizedChar.length === 0) {
            return normalizedText
        }
        return `${normalizedText}${normalizedChar}`
    }, '')

export const extractNumericAnchors = (value: string): string[] => {
    const anchors = new Set<string>()
    const matches = value.match(numericAnchorPattern) ?? []

    matches.forEach((match) => {
        const digitsOnly = match.replace(/[^\d]/gu, '')
        if (digitsOnly.length >= 4) {
            anchors.add(digitsOnly)
        }
    })

    return Array.from(anchors)
}

export const extractLineAnchors = (value: string): string[] => {
    const lineAnchors = new Set<string>()
    const lines = value
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)

    lines.forEach((line) => {
        const withoutNumbers = line
            .replace(/\d[\d.,\s€%]*/gu, ' ')
            .replace(/\s+/gu, ' ')
            .trim()
        const tokens = withoutNumbers
            .split(/\s+/u)
            .map((token) => token.replace(/[^\p{L}-]/gu, '').toLowerCase())
            .filter((token) => token.length >= 4)

        if (tokens.length === 0) {
            return
        }

        const lineAnchor = normalizeTextForMatching(tokens.slice(0, Math.min(tokens.length, 2)).join(' '))
        if (lineAnchor.length > 0) {
            lineAnchors.add(lineAnchor)
        }
    })

    return Array.from(lineAnchors)
}

export const extractKeywordAnchors = (value: string): string[] => {
    const keywordAnchors = new Set<string>()
    const keywords = value
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .flatMap((line) =>
            line
                .replace(/\d[\d.,\s€%]*/gu, ' ')
                .split(/\s+/u)
                .map((token) => token.replace(/[^\p{L}-]/gu, '').toLowerCase())
                .filter((token) => token.length >= 8)
        )

    keywords.forEach((keyword) => {
        const normalizedKeyword = normalizeTextForMatching(keyword)
        if (normalizedKeyword.length > 0) {
            keywordAnchors.add(normalizedKeyword)
        }
    })

    return Array.from(keywordAnchors)
}

export const countContainedAnchors = (normalizedText: string, anchors: string[]): number =>
    anchors.reduce((count, anchor) => (normalizedText.includes(anchor) ? count + 1 : count), 0)

export const buildGlyphs = (items: TextItem[], viewport: PageViewport): Glyph[] => {
    const glyphs: Glyph[] = []
    items.forEach((rawItem) => {
        const item = rawItem
        if (!item?.str) {
            return
        }
        const text = item.str
        if (!text) {
            return
        }

        const transform = Array.isArray(item.transform) ? item.transform : null
        const baseX = transform ? transform[4] : 0
        const baseY = transform ? transform[5] : 0
        const itemHeight = item.height ?? 0
        const itemWidth = item.width ?? 0

        if (!Number.isFinite(baseX) || Number.isNaN(baseX) || !Number.isFinite(baseY) || Number.isNaN(baseY)) {
            return
        }
        if (!Number.isFinite(itemHeight) || itemHeight <= 0) {
            return
        }

        const characters = Array.from(text)
        const charCount = Math.max(characters.length, 1)
        const perCharWidth = Number.isFinite(itemWidth) && itemWidth > 0 ? itemWidth / charCount : 0

        characters.forEach((char, index) => {
            const normalizedChar = canonicalizeChar(char)
            const startX = perCharWidth > 0 ? baseX + perCharWidth * index : baseX
            const endX = perCharWidth > 0 ? startX + perCharWidth : baseX + itemWidth
            const [x1, y1, x2, y2] = viewport.convertToViewportRectangle([startX, baseY, endX, baseY + itemHeight])
            const rectWidth = Math.abs(x2 - x1)
            const rectHeight = Math.abs(y2 - y1)
            if (!Number.isFinite(rectWidth) || !Number.isFinite(rectHeight) || rectWidth === 0 || rectHeight === 0) {
                return
            }

            const xNormalized = Math.min(x1, x2) / viewport.width
            const yNormalized = Math.min(y1, y2) / viewport.height
            const widthNormalized = rectWidth / viewport.width
            const heightNormalized = rectHeight / viewport.height

            glyphs.push({
                normalized: normalizedChar,
                isWhitespace: isWhitespaceLike(char) || normalizedChar.length === 0,
                rect: {
                    x: xNormalized,
                    y: yNormalized,
                    width: widthNormalized,
                    height: heightNormalized,
                },
            })
        })
    })
    return glyphs
}

const rectangleArea = (rect: NormalizedHighlight): number => Math.max(rect.width, 0) * Math.max(rect.height, 0)

const rectangleIntersectionArea = (left: NormalizedHighlight, right: NormalizedHighlight): number => {
    const overlapX = Math.max(0, Math.min(left.x + left.width, right.x + right.width) - Math.max(left.x, right.x))
    const overlapY = Math.max(0, Math.min(left.y + left.height, right.y + right.height) - Math.max(left.y, right.y))
    return overlapX * overlapY
}

const isCoveredByExistingRectangle = (candidate: NormalizedHighlight, existing: NormalizedHighlight): boolean => {
    const area = rectangleArea(candidate)
    if (area <= 0) {
        return true
    }
    const overlapArea = rectangleIntersectionArea(candidate, existing)
    return overlapArea / area >= 0.95
}

const removeNestedRectangles = (rectangles: NormalizedHighlight[]): NormalizedHighlight[] => {
    const sortedByArea = [...rectangles].sort((left, right) => rectangleArea(right) - rectangleArea(left))
    const result: NormalizedHighlight[] = []

    sortedByArea.forEach((candidate) => {
        const covered = result.some((existing) => isCoveredByExistingRectangle(candidate, existing))
        if (!covered) {
            result.push(candidate)
        }
    })

    return result
}

export const getBoundingRectangle = (rectangles: NormalizedHighlight[]): NormalizedHighlight | null => {
    if (rectangles.length === 0) {
        return null
    }

    let minX = Number.POSITIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxX = Number.NEGATIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY

    rectangles.forEach((rect) => {
        minX = Math.min(minX, rect.x)
        minY = Math.min(minY, rect.y)
        maxX = Math.max(maxX, rect.x + rect.width)
        maxY = Math.max(maxY, rect.y + rect.height)
    })

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
        return null
    }
    if (maxX <= minX || maxY <= minY) {
        return null
    }

    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
    }
}

export const collapseHighlightsToDominantBlock = (rectangles: NormalizedHighlight[]): NormalizedHighlight[] => {
    if (rectangles.length <= 2) {
        return rectangles
    }

    const sorted = [...rectangles].sort((a, b) => a.y - b.y || a.x - b.x)
    const clusters: NormalizedHighlight[][] = []
    const maxVerticalGap = 0.03
    const maxDominantClusterExtensionGap = 0.1
    const minHorizontalOverlapRatio = 0.15

    sorted.forEach((rect) => {
        const currentCluster = clusters[clusters.length - 1]
        if (!currentCluster) {
            clusters.push([rect])
            return
        }

        const currentMaxY = Math.max(...currentCluster.map((clusterRect) => clusterRect.y + clusterRect.height))
        const nextRectTop = rect.y
        const verticalGap = nextRectTop - currentMaxY

        if (verticalGap <= maxVerticalGap) {
            currentCluster.push(rect)
            return
        }

        clusters.push([rect])
    })

    const rankedClusters = clusters
        .map((clusterRects, index) => ({
            index,
            clusterRects,
            rectCount: clusterRects.length,
            area: clusterRects.reduce((totalArea, rect) => totalArea + rectangleArea(rect), 0),
            bounds: getBoundingRectangle(clusterRects),
        }))
        .filter(
            (
                cluster
            ): cluster is {
                index: number
                clusterRects: NormalizedHighlight[]
                rectCount: number
                area: number
                bounds: NormalizedHighlight
            } => Boolean(cluster.bounds)
        )
        .sort((a, b) => {
            if (b.rectCount !== a.rectCount) {
                return b.rectCount - a.rectCount
            }
            return b.area - a.area
        })

    const dominantCluster = rankedClusters[0]
    if (!dominantCluster) {
        return rectangles
    }

    const selectedClusterIndexes = new Set<number>([dominantCluster.index])
    let selectedBounds = dominantCluster.bounds
    let shouldExpand = true

    while (shouldExpand) {
        shouldExpand = false

        rankedClusters.forEach((cluster) => {
            if (selectedClusterIndexes.has(cluster.index)) {
                return
            }

            const overlapX = Math.max(
                0,
                Math.min(selectedBounds.x + selectedBounds.width, cluster.bounds.x + cluster.bounds.width) -
                    Math.max(selectedBounds.x, cluster.bounds.x)
            )
            const referenceWidth = Math.min(selectedBounds.width, cluster.bounds.width)
            const horizontalOverlapRatio = referenceWidth > 0 ? overlapX / referenceWidth : 0

            const verticalGapAbove = selectedBounds.y - (cluster.bounds.y + cluster.bounds.height)
            const verticalGapBelow = cluster.bounds.y - (selectedBounds.y + selectedBounds.height)
            const verticalGap = Math.max(verticalGapAbove, verticalGapBelow, 0)

            if (horizontalOverlapRatio < minHorizontalOverlapRatio || verticalGap > maxDominantClusterExtensionGap) {
                return
            }

            selectedClusterIndexes.add(cluster.index)
            const selectedRects = clusters.filter((_, index) => selectedClusterIndexes.has(index)).flat()
            const nextBounds = getBoundingRectangle(selectedRects)
            if (nextBounds) {
                selectedBounds = nextBounds
            }
            shouldExpand = true
        })
    }

    const selectedRects = clusters.filter((_, index) => selectedClusterIndexes.has(index)).flat()
    const blockRect = getBoundingRectangle(selectedRects)
    return blockRect ? [blockRect] : rectangles
}

export const matchVariantRectangles = (glyphs: Glyph[], variants: string[]): NormalizedHighlight[] => {
    if (glyphs.length === 0 || variants.length === 0) {
        return []
    }

    const rectangles: NormalizedHighlight[] = []
    const seen = new Set<string>()

    variants.forEach((variantRaw) => {
        const variantTokens = Array.from(variantRaw).map((char) => ({
            normalized: canonicalizeChar(char),
            skippable: isSkippableVariantChar(char),
        }))
        const variantHasContent = variantTokens.some((token) => !token.skippable && token.normalized.length > 0)
        if (!variantHasContent) {
            return
        }

        for (let start = 0; start < glyphs.length; start += 1) {
            let glyphIndex = start
            while (glyphIndex < glyphs.length && (glyphs[glyphIndex].isWhitespace || glyphs[glyphIndex].normalized.length === 0)) {
                glyphIndex += 1
            }
            if (glyphIndex >= glyphs.length) {
                break
            }

            const firstVariantIndex = variantTokens.findIndex((token) => !token.skippable && token.normalized.length > 0)
            if (firstVariantIndex === -1 || glyphs[glyphIndex].normalized !== variantTokens[firstVariantIndex].normalized) {
                continue
            }

            let variantIndex = 0
            let currentGlyphIndex = glyphIndex
            let matchStart = -1

            while (currentGlyphIndex < glyphs.length && variantIndex < variantTokens.length) {
                const variantToken = variantTokens[variantIndex]
                if (variantToken.skippable || variantToken.normalized.length === 0) {
                    variantIndex += 1
                    continue
                }

                const glyph = glyphs[currentGlyphIndex]
                if (glyph.isWhitespace || glyph.normalized.length === 0) {
                    currentGlyphIndex += 1
                    continue
                }

                if (glyph.normalized === variantToken.normalized) {
                    if (matchStart === -1) {
                        matchStart = currentGlyphIndex
                    }
                    currentGlyphIndex += 1
                    variantIndex += 1
                    continue
                }

                matchStart = -1
                break
            }

            if (matchStart !== -1 && variantIndex >= variantTokens.length) {
                const matchEnd = currentGlyphIndex - 1
                let minX = Number.POSITIVE_INFINITY
                let minY = Number.POSITIVE_INFINITY
                let maxX = Number.NEGATIVE_INFINITY
                let maxY = Number.NEGATIVE_INFINITY

                for (let idx = matchStart; idx <= matchEnd; idx += 1) {
                    const rect = glyphs[idx].rect
                    minX = Math.min(minX, rect.x)
                    minY = Math.min(minY, rect.y)
                    maxX = Math.max(maxX, rect.x + rect.width)
                    maxY = Math.max(maxY, rect.y + rect.height)
                }

                if (
                    Number.isFinite(minX) &&
                    Number.isFinite(minY) &&
                    Number.isFinite(maxX) &&
                    Number.isFinite(maxY) &&
                    maxX > minX &&
                    maxY > minY
                ) {
                    const key = `${minX.toFixed(6)}-${minY.toFixed(6)}-${maxX.toFixed(6)}-${maxY.toFixed(6)}`
                    if (!seen.has(key)) {
                        seen.add(key)
                        rectangles.push({
                            x: minX,
                            y: minY,
                            width: maxX - minX,
                            height: maxY - minY,
                        })
                    }
                }
            }
        }
    })

    return removeNestedRectangles(rectangles)
}

export const buildSearchVariants = (term: string): string[] => {
    const base = term.toLowerCase()
    const variants = new Set<string>([base])
    const containsLetters = /\p{L}/u.test(term)
    const lineSegments = term
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)

    if (lineSegments.length > 1) {
        lineSegments.forEach((line) => {
            variants.add(line.toLowerCase())
        })

        lineSegments
            .map((line) => line.split(/\d/u)[0]?.trim() ?? '')
            .filter((linePrefix) => linePrefix.length > 0)
            .forEach((linePrefix) => {
                variants.add(linePrefix.toLowerCase())

                linePrefix
                    .split(/\s+/u)
                    .map((token) => token.replace(/[^\p{L}-]/gu, ''))
                    .filter((token) => token.length >= 14)
                    .forEach((token) => variants.add(token.toLowerCase()))
            })
    }

    const noWhitespace = base.replace(whitespaceStrippingPattern, '')
    if (noWhitespace && noWhitespace !== base) {
        variants.add(noWhitespace)
    }

    const dashNormalized = base.replace(dashVariantPattern, '-')
    if (dashNormalized !== base) {
        variants.add(dashNormalized)
        const dashNoWhitespace = dashNormalized.replace(whitespaceStrippingPattern, '')
        if (dashNoWhitespace && dashNoWhitespace !== dashNormalized) {
            variants.add(dashNoWhitespace)
        }
    }

    const withoutThousands = base.replace(thousandsSeparatorPattern, '')
    if (withoutThousands && withoutThousands !== base) {
        variants.add(withoutThousands)
        const withoutThousandsWhitespace = withoutThousands.replace(whitespaceStrippingPattern, '')
        if (withoutThousandsWhitespace && withoutThousandsWhitespace !== withoutThousands) {
            variants.add(withoutThousandsWhitespace)
        }
    }

    if (!containsLetters) {
        const numberMatch = term.match(/^([\d.,\s'’´`\u00a0\u202f\u2009-]+)\s*([€£$%/\w]*)/u)
        if (numberMatch?.[1]) {
            const numberPartRaw = numberMatch[1]
            const numberPart = numberPartRaw.trim()
            const normalizedNumberVariants = [
                numberPart,
                numberPart.replace(whitespaceStrippingPattern, ''),
                numberPart.replace(thousandsSeparatorPattern, ''),
                numberPart.replace(/[.,]/g, '').replace(whitespaceStrippingPattern, ''),
                numberPart.replace(thousandsSeparatorPattern, '').replace(/[.,]/g, ''),
                numberPart.replace(/\./g, '').replace(/,/g, '.').replace(whitespaceStrippingPattern, ''),
                numberPart.replace(thousandsSeparatorPattern, '').replace(/\./g, '').replace(/,/g, '.'),
            ]
            normalizedNumberVariants.forEach((variant) => {
                if (variant) {
                    variants.add(variant.toLowerCase())
                }
            })
        }

        const currencyMatch = term.match(/([\d.,\s'’´`\u00a0\u202f\u2009-]+)\s*([€$£])/u)
        if (currencyMatch) {
            const valuePart = currencyMatch[1].trim()
            const symbol = currencyMatch[2]
            if (valuePart) {
                const valueLower = valuePart.toLowerCase()
                const valueNoWhitespace = valueLower.replace(whitespaceStrippingPattern, '')
                const valueNoThousands = valueLower.replace(thousandsSeparatorPattern, '')
                const symbolLower = symbol.toLowerCase()
                variants.add(`${valueLower} ${symbolLower}`)
                variants.add(`${valueNoWhitespace}${symbolLower}`)
                variants.add(`${valueLower}${symbolLower}`)
                if (valueNoThousands && valueNoThousands !== valueLower) {
                    variants.add(`${valueNoThousands}${symbolLower}`)
                    variants.add(`${valueNoThousands} ${symbolLower}`)
                }
            }
        }

        const euroWordMatch = term.match(/([\d.,\s'’´`\u00a0\u202f\u2009-]+)\s*(euro|eur)/iu)
        if (euroWordMatch) {
            const valuePart = euroWordMatch[1].trim()
            const unit = euroWordMatch[2].toLowerCase()
            if (valuePart) {
                const valueLower = valuePart.toLowerCase()
                const valueNoWhitespace = valueLower.replace(whitespaceStrippingPattern, '')
                const valueNoThousands = valueLower.replace(thousandsSeparatorPattern, '')
                variants.add(`${valueLower} ${unit}`)
                variants.add(`${valueNoWhitespace}${unit}`)
                variants.add(`${valueLower}${unit}`)
                if (valueNoThousands && valueNoThousands !== valueLower) {
                    variants.add(`${valueNoThousands}${unit}`)
                    variants.add(`${valueNoThousands} ${unit}`)
                }
            }
        }
    }

    return Array.from(variants)
}

export const shouldPreferBlockFocusHighlight = (term: string): boolean => {
    const lineSegments = term
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    if (lineSegments.length >= 2) {
        return true
    }

    const numericAnchors = extractNumericAnchors(term)
    return numericAnchors.length >= 2 && term.trim().length >= 80
}

export const extractFocusBlockLines = (term: string): string[] =>
    term
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length >= 8)
